import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import Timeline, {
  TimelineHeaders,
  SidebarHeader,
  CustomHeader,
  TimelineMarkers,
  CustomMarker,
} from "react-calendar-timeline";
import 'react-calendar-timeline/lib/Timeline.css';
import {
  GET_ALL_ROOMS,
  GET_ALL_VALID_BOOKINGS,
  UPDATE_BOOKING_ROOM_BY_BOOKING_ID_AND_ROOM_ID,
  GET_BOOKING_DETAILS_BY_BOOKING_ID
} from 'graphql/service';
import moment from 'moment-timezone';
import { debounce } from 'lodash-es';
import Spinner from 'components/Spinner';
import SubHeader from './components/Timeline/SubHeader';
import ItemRenderer from './components/Timeline/ItemRenderer';
import GroupRenderer from './components/Timeline/GroupRenderer';
import GroupLabel from './components/Timeline/GroupLabel';
import ClientModal from './components/ClientModal';
import { BookingTimelineContainer } from 'screens/Bookings/styles';
import SideBarTitleContainer from 'screens/Bookings/components/Timeline/SideBarTitleContainer';
import TimelineHeader from 'screens/Bookings/components/Timeline/TimelineHeader';
import colors from 'constants/colors';
import { TIME_ZONE } from 'constants/constant';
import { debouncedErrorMessage } from 'utils/messageUtil';
import { retry } from "utils/util";

const DAY = 24 * 60 * 60 * 1000;
const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
const windowSize = Math.max(Math.floor(width / 120), 7);
const FETCH_BUFFER = DAY * windowSize;

const keys = {
  groupIdKey: "id",
  groupTitleKey: "title",
  groupRightTitleKey: "rightTitle",
  itemIdKey: "id",
  itemTitleKey: "title",
  itemDivTitleKey: "title",
  itemGroupKey: "group",
  itemTimeStartKey: "start",
  itemTimeEndKey: "end",
  groupLabelKey: "title",
};

const buildTimelineItem = booking => ({
  id: booking.bookingId,
  group: booking.roomBookings?.[0]?.room?.roomNumber,
  groupId: booking.group?.groupId,
  status: booking.status,
  title: booking.group?.name || booking.fullName,
  start: moment(booking.checkedInAt || booking.checkInDate).tz(TIME_ZONE).valueOf(),
  end: moment(booking.checkedOutAt || booking.checkOutDate).tz(TIME_ZONE).valueOf(),
  canMove: true,
  canResize: false,
  canChangeGroup: true,
});

const Bookings = () => {

  const navigate = useNavigate();
  const pickedDate = useSelector(state => state.pickedDateReducer.pickedDate);

  // Modal state
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [bookingId, setBookingId] = useState('');
  const [bookingDetailsData, setBookingDetailsData] = useState({});

  // Timeline state
  const [items, setItems] = useState([]);
  const [timelineState, setTimelineState] = useState({
    visibleTimeStart: null,
    visibleTimeEnd: null,
    today: null,
    endToday: null,
  });
  const loadedRangeRef = useRef({ start: null, end: null });
  
  // GraphQL queries
  const { data: roomsData = { validRooms: [] }, error: roomsError } = useQuery(GET_ALL_ROOMS, { fetchPolicy: 'cache-first' });
  const [getValidBookingsByDateRange, { error: bookingsError }] = useLazyQuery(GET_ALL_VALID_BOOKINGS, { fetchPolicy: 'cache-first' });
  const [getBookingDetailsByBookingId, { data, error }] = useLazyQuery(GET_BOOKING_DETAILS_BY_BOOKING_ID);
  const [updateRoomBooking] = useMutation(UPDATE_BOOKING_ROOM_BY_BOOKING_ID_AND_ROOM_ID, {
    onError: error => {
      if (!error?.networkError) {
        let content = error.message;
        if (error.message.includes('Validation')) {
          content = JSON.stringify(error.graphQLErrors[0]?.extensions?.exception);
        }
        debouncedErrorMessage(content);
      }
    },
  });

  // Handle booking details
  useEffect(() => {
    if (data) setBookingDetailsData(data);
  }, [data]);

  useEffect(() => {
    if (bookingId) {
      getBookingDetailsByBookingId({ variables: { bookingId } });
    }
  }, [bookingId, getBookingDetailsByBookingId]);

  useEffect(() => {
    if (!error && !bookingsError && !roomsError) return;
    if (!error.networkError && !bookingsError.networkError && !roomsError.networkError) return;{
      let content = error.message || bookingsError.message || roomsError.message;
      if (error.message.includes('Validation') || bookingsError.message.includes('Validation') || roomsError.message.includes('Validation')) {
        content = JSON.stringify(error.graphQLErrors[0]?.extensions?.exception) ||
                  JSON.stringify(bookingsError.graphQLErrors[0]?.extensions?.exception) ||
                  JSON.stringify(roomsError.graphQLErrors[0]?.extensions?.exception);
      }
      debouncedErrorMessage(content);
    }
  }, [error, bookingsError, roomsError]);

  // Build groups from rooms
  const { groups, GroupLabels } = useMemo(() => {
    // If data is missing, return empty arrays immediately.
    if (!roomsData?.validRooms?.length) {
      return { groups: [], GroupLabels: [] };
    }

    const sortedRooms = [...roomsData.validRooms].sort((a, b) => Number(a.roomNumber) - Number(b.roomNumber));
    
    // An intermediate map to group by room type
    const groupedByType = {};
    sortedRooms.forEach(room => {
      if (room.roomType.name === 'Amenities') return;
      const type = room.roomType.name;
      groupedByType[type] = groupedByType[type] || [];
      groupedByType[type].push({
        id: room.roomNumber,
        title: room.roomNumber,
        roomId: room.roomId,
        roomTypeName: type,
        status: room.roomStatusHistories?.[0]?.status,
        height: 32,
      });
    });

    // Final outputs for the timeline
    const finalGroups = [];
    const finalGroupLabels = [];
    let top = 152; // Starting top offset for the labels

    for (const key in groupedByType) {
      const currentTypeGroups = groupedByType[key];
      
      // Flatten the individual room items into the groups array
      currentTypeGroups.forEach(group => {
        finalGroups.push(group);
      });
      
      // Generate the overlay label
      finalGroupLabels.push(
        <GroupLabel 
          key={key} 
          groupName={key} 
          height={currentTypeGroups.length * 32 - 1} 
          top={top} 
        />
      );
      
      // Update the top offset for the next group label
      top += currentTypeGroups.length * 32;
    }

    return { 
      groups: finalGroups, 
      GroupLabels: finalGroupLabels 
    };

  }, [roomsData]); // Re-calculate only when roomsData changes


  // Merge bookings into items (no duplicates)
  const mergeItems = useCallback(
    incomingBookings => {
      if (!incomingBookings?.length || !groups.length) return;

      setItems(prev => {
        const incomingItems = incomingBookings
          .map(buildTimelineItem)
          .filter(item => groups.some(r => r.id === item.group));

        const itemMap = new Map(prev.map(i => [i.id, i]));
        incomingItems.forEach(i => itemMap.set(i.id, i));

        return Array.from(itemMap.values());
      });
    },
    [groups]
  );

  // Initial load
  useEffect(() => {
    if (!pickedDate || !groups.length) return;

    // Compute initial visible range *before any render*
    const centerTime = moment(pickedDate).tz(TIME_ZONE).startOf('day').valueOf();

    const initialVisibleStart = centerTime - (FETCH_BUFFER / 2);
    const initialVisibleEnd   = centerTime + (FETCH_BUFFER / 2);

    setTimelineState({
      visibleTimeStart: initialVisibleStart,
      visibleTimeEnd: initialVisibleEnd,
      today: moment(pickedDate).tz(TIME_ZONE).startOf('day').valueOf(),
      endToday: moment(pickedDate).tz(TIME_ZONE).endOf('day').valueOf(),
    });
    
    // Load data with buffers on BOTH sides
    const loadStart = initialVisibleStart - FETCH_BUFFER;
    const loadEnd = initialVisibleEnd + FETCH_BUFFER;

    getValidBookingsByDateRange({
      variables: {
        filters: { dateFrom: new Date(loadStart), dateTo: new Date(loadEnd) },
        pagination: { limit: 1000, offset: 0 },
      },
    }).then(({ data }) => {
      const incoming = data?.validBookingsPaginated?.items;
      mergeItems(incoming);
      loadedRangeRef.current = { start: loadStart, end: loadEnd };
    });
  }, [pickedDate, groups, getValidBookingsByDateRange, mergeItems]);

  // Fetch more bookings on scroll
  const fetchBookings = useCallback(
    debounce((start, end) => {
      if (!start || !end) return;

      const range = loadedRangeRef.current;
      if (!range.start || !range.end) return;

      // Determine what to fetch
      let fetchStart = start;
      let fetchEnd = end;

      // If already loaded, skip
      if (start >= range.start && end <= range.end) return;

      // Fetch left side
      if (start < range.start) {
        fetchStart = start - FETCH_BUFFER;
        fetchEnd = range.start;
      }
      // Fetch right side
      else if (end > range.end) {
        fetchStart = range.end;
        fetchEnd = end + FETCH_BUFFER;
      }

      getValidBookingsByDateRange({
        variables: {
          filters: { dateFrom: new Date(fetchStart), dateTo: new Date(fetchEnd) },
          pagination: { limit: 1000, offset: 0 },
        },
      }).then(({ data }) => {
        const incoming = data?.validBookingsPaginated?.items || [];
        mergeItems(incoming);
        loadedRangeRef.current = {
          start: Math.min(range.start, fetchStart),
          end: Math.max(range.end, fetchEnd),
        };
      });
    }, { leading: true, trailing: true, maxWait: 2000 }, 300),
    [getValidBookingsByDateRange, mergeItems]
  );

  // Handle timeline scrolling
  const handleTimeChange = useCallback(
    (newVisibleStart, newVisibleEnd, updateScrollCanvas) => {
      // Always update canvas first for smooth scrolling
      updateScrollCanvas(newVisibleStart, newVisibleEnd);
      setTimelineState(prev => ({
        ...prev,
        visibleTimeStart: newVisibleStart,
        visibleTimeEnd: newVisibleEnd,
      }));

      const { start: loadedStart, end: loadedEnd } = loadedRangeRef.current;
      if (loadedStart === null || loadedEnd === null) return;

      // Load more data when approaching boundaries
      if (newVisibleStart < loadedStart + FETCH_BUFFER) {
        fetchBookings(newVisibleStart - FETCH_BUFFER, loadedStart);
      }
      if (newVisibleEnd > loadedEnd - FETCH_BUFFER) {
        fetchBookings(loadedEnd, newVisibleEnd + FETCH_BUFFER);
      }
    },
    [fetchBookings]
  );

  // Item handlers
  const handleItemClick = (itemId) => {
    setBookingId(itemId);
    setClientModalOpen(true);
  };

  const handleItemDoubleClick = (itemId, e, time, item) => {
    const groupId = item.groupId;
    navigate(`/booking-details/${groupId}`);
  };

  const handleItemMove = async (itemId, dragTime, newGroupOrder) => {
    const originItems = [...items];
    const item = originItems.find(i => i.id === itemId);
    const newItems = originItems.filter(i => i.id !== itemId);
    const prevGroupIndex = groups.findIndex(g => g.id === item.group);
    const label = groups.find(g => g.id === item.group)?.roomTypeName;
    const roomTypeName = groups[newGroupOrder].roomTypeName;
    const roomId = groups[newGroupOrder].roomId;

    if (newGroupOrder === prevGroupIndex) return;
    if (label !== roomTypeName) {
      debouncedErrorMessage('Cannot move booking to another room type');
      return;
    }

    newItems.push({ ...item, group: groups[newGroupOrder].id });
    setItems(newItems);
    setBookingDetailsData(prev => ({
      ...prev,
      bookingByID: {
        ...prev.bookingByID,
        roomBookings: [
          { ...prev.bookingByID?.roomBookings?.[0], room: { roomNumber: groups[newGroupOrder].id } },
          ...prev.bookingByID?.roomBookings,
        ],
      },
    }));

    try {
      await retry(() =>
        updateRoomBooking({ variables: { input: { roomId, bookingId: itemId } } })
      );
    } catch (err) {
      setItems(originItems);
      setBookingDetailsData(prev => ({
        ...prev,
        bookingByID: { ...prev.bookingByID, roomBookings: [...prev.bookingByID?.roomBookings].slice(1) },
      }));
      if (!err.networkError) {
        let content = err.message;
        if (err.message.includes('Validation')) content = JSON.stringify(err.graphQLErrors[0]?.extensions?.exception);
        debouncedErrorMessage(content);
      }
    }
  };

  // Stable renderers
  const itemRenderer = useCallback((props) => <ItemRenderer {...props} />, []);
  const groupRenderer = useCallback((props) => <GroupRenderer {...props} />, []);

  const sidebarHeaderRenderer = useCallback(
    ({ getRootProps }) => <SideBarTitleContainer getRootProps={getRootProps} />,
    []
  );
  const customHeaderRenderer = useCallback(
    ({ headerContext: { intervals }, getRootProps, getIntervalProps }) =>
      TimelineHeader({ intervals, getRootProps, getIntervalProps }),
    []
  );

  const ready =
    groups.length > 0 &&
    items.length > 0 &&
    timelineState.visibleTimeStart !== null &&
    timelineState.visibleTimeEnd !== null;

  if (!ready) return <Spinner />;

  return (
    <>
      <Helmet>
        <title>Bookings - Rook Hotel PMS</title>
        <meta name="description" content="Bookings - Rook Hotel PMS" />
      </Helmet>
      <SubHeader setTimelineState={setTimelineState} />
      <BookingTimelineContainer fixedHeader>
        <Timeline
          keys={keys}
          groups={groups}
          items={items}
          fullUpdate={false}
          stackItems={false}
          itemTouchSendsClick={false}
          sidebarWidth={100}
          itemHeightRatio={1}
          traditionalZoom={true}
          itemRenderer={itemRenderer}
          groupRenderer={groupRenderer}
          onItemClick={handleItemClick}
          onItemSelect={handleItemClick}
          onItemMove={handleItemMove}
          onItemDoubleClick={handleItemDoubleClick}
          visibleTimeStart={timelineState.visibleTimeStart}
          visibleTimeEnd={timelineState.visibleTimeEnd}
          onTimeChange={handleTimeChange}
          timeSteps={{ day: 1 }}
          minZoom={DAY * 3}
          maxZoom={DAY * 30}
        >
          <TimelineHeaders calendarHeaderClassName="timelineHeader">
            <SidebarHeader>{sidebarHeaderRenderer}</SidebarHeader>
            <CustomHeader height={50} unit="day">
              {customHeaderRenderer}
            </CustomHeader>
          </TimelineHeaders>
          <TimelineMarkers>
            <CustomMarker date={timelineState.today}>
              {({ styles }) => (
                <div style={{ ...styles, width: 1, backgroundColor: colors.green, zIndex: 200 }} />
              )}
            </CustomMarker>
            <CustomMarker date={timelineState.endToday}>
              {({ styles }) => (
                <div style={{ ...styles, width: 1, backgroundColor: colors.green, zIndex: 200 }} />
              )}
            </CustomMarker>
          </TimelineMarkers>
        </Timeline>
        {ClientModal && (
          <ClientModal
            clientModalOpen={clientModalOpen}
            setClientModalOpen={setClientModalOpen}
            bookingDetailsData={bookingDetailsData}
            setBookingDetailsData={setBookingDetailsData}
          />
        )}
      </BookingTimelineContainer>
      {GroupLabels}
    </>
  );
};

export default Bookings;